// worlds.js: owns the world instances ("slots"). The nebula module serves
// two slots (ignite and brand), each its own instance, so neither has to
// switch modes mid-film. Slots load lazily in film order, warm their shaders
// up offscreen (a few small renders spread over frames, so no single frame
// stalls), and every call into a world is wrapped in try/catch. A world that
// fails to load, times out, throws repeatedly or fails to compile is swapped
// for a cheap fallback world: one broken module can never take the site down.
import * as THREE from 'three';
import * as Nebula from '../worlds/nebula.js';
import * as Earth from '../worlds/earth.js';
import * as Galaxy from '../worlds/galaxy.js';
import * as Blackhole from '../worlds/blackhole.js';
// v7 HAUTE COSMOS worlds: each starts as a stub that re-exports the v5 world it replaces (web/src/worlds/V7.md).
// ?v5w loads the v5 worlds instead, for A/B.
import * as Gem from '../worlds/gem.js';
import * as Sapphire from '../worlds/sapphire.js';
import * as Brooch from '../worlds/brooch.js';
import * as Onyx from '../worlds/onyx.js';
import { createFallback } from './fallback.js';
import { withTimeout } from './util.js';

const MODULES = { nebula: Nebula, earth: Earth, galaxy: Galaxy, blackhole: Blackhole, gem: Gem, sapphire: Sapphire, brooch: Brooch, onyx: Onyx };
const V5W = typeof location !== 'undefined' && /[?&]v5w\b/.test(location.search);
export const SLOTS = {
  ignite: { mod: V5W ? 'nebula' : 'gem', params: { mode: 'ignite' } },
  earth: { mod: V5W ? 'earth' : 'sapphire', params: {} },
  galaxy: { mod: V5W ? 'galaxy' : 'brooch', params: {} },
  blackhole: { mod: V5W ? 'blackhole' : 'onyx', params: {} },
  brand: { mod: V5W ? 'nebula' : 'gem', params: { mode: 'brand', brand: 'Starlex' } },
};
export const SLOT_NAMES = Object.keys(SLOTS);
const LOAD_TIMEOUT = 30000;
const MAX_STRIKES = 3; // consecutive throwing frames before a world is replaced
const WARM_STEPS = [0, 0.5, 1, 0.25];

export class Worlds {
  constructor(renderer, opts, log) {
    this.r = renderer;
    this.opts = opts; // { tier, half, mobile, cfg, base }
    this.log = log || (() => {});
    this.entries = {};
    this.w = 1; this.h = 1;
    this.queue = [];
    this.warmRT = new THREE.WebGLRenderTarget(192, 108, { type: opts.half ? THREE.HalfFloatType : THREE.UnsignedByteType, depthBuffer: true });
    this.current = null; // slot being driven (for shader-error attribution)
    renderer.debug.checkShaderErrors = true;
    renderer.debug.onShaderError = (gl, program, vs, fs) => {
      const name = this.current || 'unknown';
      let info = '';
      try { info = `${gl.getProgramInfoLog(program) || ''} ${gl.getShaderInfoLog(fs) || ''} ${gl.getShaderInfoLog(vs) || ''}`; } catch (e) { /* lost context */ }
      console.error(`[v5] world "${name}" shader failed to compile: ${info.trim().slice(0, 500)}`);
      const e = this.entries[name];
      if (e && e.world && !e.world.isFallback) e.shaderFailed = true;
    };
  }

  entry(slot) {
    if (!this.entries[slot]) this.entries[slot] = { slot, status: 'idle', world: null, warm: 0, strikes: 0, error: null, size: '', promise: null, t0: 0, ms: 0, shaderFailed: false };
    return this.entries[slot];
  }

  status() {
    const o = {};
    for (const k of SLOT_NAMES) {
      const e = this.entries[k];
      o[k] = e ? { status: e.status, fallback: !!(e.world && e.world.isFallback), error: e.error, ms: Math.round(e.ms), anchors: e.world && e.world.anchors ? Object.keys(e.world.anchors) : [] } : { status: 'idle' };
    }
    return o;
  }

  setSize(w, h) { this.w = w; this.h = h; }

  load(slot) {
    const e = this.entry(slot);
    if (e.promise) return e.promise;
    e.status = 'loading';
    e.t0 = performance.now();
    e.promise = (async () => {
      try {
        const def = SLOTS[slot];
        const mod = def && MODULES[def.mod];
        if (!mod || typeof mod.create !== 'function') throw new Error('module has no create()');
        this.current = slot;
        e.world = mod.create(this.r, { ...this.opts });
        this.current = null;
        if (!e.world || typeof e.world.render !== 'function' || typeof e.world.update !== 'function') throw new Error('create() did not return a world');
        if (e.world.load) await withTimeout(e.world.load(), LOAD_TIMEOUT, `${slot}.load()`);
        // parallel shader compile where the world exposes its scene (KHR_parallel_shader_compile)
        if (e.world.scene && e.world.camera && this.r.compileAsync) {
          try { await withTimeout(this.r.compileAsync(e.world.scene, e.world.camera), 8000, 'compileAsync'); } catch (err) { /* warm-up renders will compile */ }
        }
        if (e.shaderFailed) throw new Error('shader compile error');
        e.status = 'loaded';
      } catch (err) {
        this.current = null;
        this.fail(e, err, 'load');
      }
      e.ms = performance.now() - e.t0;
      return e;
    })();
    return e.promise;
  }

  fail(e, err, where) {
    const msg = `${where}: ${(err && err.message) || err}`;
    console.error(`[v5] world "${e.slot}" failed (${msg}); showing a fallback`);
    e.error = msg;
    if (e.world && !e.world.isFallback) { try { e.world.dispose && e.world.dispose(); } catch (x) { /* ignore */ } }
    e.world = createFallback(this.r, SLOTS[e.slot] ? SLOTS[e.slot].mod : 'nebula');
    e.size = '';
    e.warm = 0;
    e.strikes = 0;
    e.shaderFailed = false;
    if (e.status === 'ready') { this.ensureSize(e); } else e.status = 'loaded';
  }

  ready(slot) { const e = this.entries[slot]; return !!e && e.status === 'ready'; }
  get(slot) { const e = this.entries[slot]; return e && e.world; }

  ensureSize(e) {
    const key = `${this.w}x${this.h}`;
    if (e.size === key) return;
    e.size = key;
    this.guard(e, 'resize', () => { if (e.world.resize) e.world.resize(this.w, this.h); });
  }

  guard(e, where, fn) {
    this.current = e.slot;
    try {
      fn();
      this.current = null;
      if (e.shaderFailed) { this.fail(e, new Error('shader compile error'), where); return false; }
      return true;
    } catch (err) {
      this.current = null;
      e.strikes++;
      if (where === 'warm' || where === 'resize' || e.strikes >= MAX_STRIKES) this.fail(e, err, where);
      else console.warn(`[v5] world "${e.slot}" ${where} threw (${e.strikes}/${MAX_STRIKES}):`, err);
      return false;
    }
  }

  // queue slots for warm-up (priority order) and start loading them
  prepare(slots) {
    for (const s of slots) {
      if (!this.queue.includes(s) && !this.ready(s)) this.queue.push(s);
      this.load(s);
    }
  }

  pending() { return this.queue.length > 0; }

  // One warm-up render (at most one per frame, called by the main loop).
  // Returns true if it did GPU work this frame.
  warmStep(inputs) {
    for (const slot of this.queue) {
      const e = this.entries[slot];
      if (!e || e.status !== 'loaded') continue;
      this.ensureSize(e);
      const input = inputs[slot];
      const saved = input.progress, savedV = input.velocity;
      input.progress = WARM_STEPS[e.warm]; input.velocity = 0;
      this.guard(e, 'warm', () => { e.world.update(0, 0, input); e.world.render(this.warmRT); });
      input.progress = saved; input.velocity = savedV;
      this.r.autoClear = true;
      e.warm++;
      if (e.warm >= WARM_STEPS.length || e.world.isFallback) {
        e.status = 'ready';
        e.ms = performance.now() - e.t0;
        this.log(`world ${slot} ready in ${Math.round(e.ms)} ms${e.world.isFallback ? ' (FALLBACK: ' + e.error + ')' : ''}`);
        this.queue = this.queue.filter((q) => q !== slot);
      }
      return true;
    }
    return false;
  }

  update(slot, dt, time, input) {
    const e = this.entries[slot];
    if (!e || e.status !== 'ready') return false;
    this.ensureSize(e);
    return this.guard(e, 'update', () => e.world.update(dt, time, input));
  }

  render(slot, target) {
    const e = this.entries[slot];
    if (!e || !e.world) return false;
    const ok = this.guard(e, 'render', () => e.world.render(target));
    if (ok) e.strikes = 0;
    return ok;
  }

  dispose() {
    for (const k in this.entries) { const e = this.entries[k]; try { e.world && e.world.dispose && e.world.dispose(); } catch (x) { /* ignore */ } }
    this.warmRT.dispose();
  }
}
